

/**
 * Standardized Error Codes for VoodPortal
 * 
 * These codes are used between server actions and client-side error handling
 * to provide stable, predictable error responses.
 */

export const ErrorCode = {
    // Authentication & Authorization
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',

    // Validation
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    INVALID_INPUT: 'INVALID_INPUT',

    // Database / Resources
    NOT_FOUND: 'NOT_FOUND',
    DUPLICATE_RECORD: 'DUPLICATE_RECORD',
    EMAIL_TAKEN: 'EMAIL_TAKEN',
    PHONE_TAKEN: 'PHONE_TAKEN',
    FOREIGN_KEY_CONSTRAINT: 'FOREIGN_KEY_CONSTRAINT',
    DATABASE_ERROR: 'DATABASE_ERROR',

    // Account Status
    ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',

    // Brand / Multi-tenancy
    BRAND_ACCESS_DENIED: 'BRAND_ACCESS_DENIED',
    /** A Forms website with no brand mapping: nothing brand-scoped can run on its requests. */
    FORM_SOURCE_UNMAPPED: 'FORM_SOURCE_UNMAPPED',

    // Email & Configuration
    EMAIL_NOT_CONFIGURED: 'EMAIL_NOT_CONFIGURED',
    EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
    CONFIGURATION_MISSING: 'CONFIGURATION_MISSING',

    // Subscription Business Logic
    SUBSCRIPTION_EXISTS: 'SUBSCRIPTION_EXISTS',
    TRIAL_ALREADY_USED: 'TRIAL_ALREADY_USED',
    USERNAME_TAKEN: 'USERNAME_TAKEN',
    M3U_URL_REQUIRED: 'M3U_URL_REQUIRED',
    CUSTOMER_NO_EMAIL: 'CUSTOMER_NO_EMAIL',
    /** A manual resend inside the window after the last welcome; the operator may override it. */
    ALREADY_RESENT: 'ALREADY_RESENT',
    /** A send to this customer is still with the provider; even an override waits for it. */
    SEND_IN_PROGRESS: 'SEND_IN_PROGRESS',

    // Email templates
    TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',

    // Network / External Services
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT: 'TIMEOUT',
    SMTP_PRE_ACCEPTANCE_TIMEOUT: 'SMTP_PRE_ACCEPTANCE_TIMEOUT',
    EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
    FORMS_UNAVAILABLE: 'FORMS_UNAVAILABLE',

    // Generic
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCodeType = typeof ErrorCode[keyof typeof ErrorCode];

/**
 * Nested error object for action results
 */
export type ActionError = {
    code: ErrorCodeType;
    message?: string; // Optional custom message, falls back to ErrorMessages lookup
};

/**
 * Standardized response type for server actions
 * 
 * Use this instead of manually defining { success: boolean; data?: T; error?: string }
 * The discriminated union allows TypeScript to narrow types based on success.
 */
export type ActionResult<T = void> =
    | { success: true; data?: T }
    | { success: false; error: ActionError };

/**
 * Application Error class with stable error codes
 * 
 * Use this instead of throwing plain Error objects in server actions
 * to ensure predictable client-side error handling.
 */
export class AppError extends Error {
    public readonly code: ErrorCodeType;
    public readonly userMessage: string;
    public readonly technicalDetails?: unknown;

    constructor(
        code: ErrorCodeType,
        userMessage: string,
        technicalDetails?: unknown
    ) {
        super(userMessage);
        this.name = 'AppError';
        this.code = code;
        this.userMessage = userMessage;
        this.technicalDetails = technicalDetails;
    }

    /**
     * Convert to a plain object for JSON serialization
     */
    toJSON() {
        return {
            code: this.code,
            message: this.userMessage,
        };
    }
}

/**
 * Helper to create an AppError
 */
export function createAppError(
    code: ErrorCodeType,
    userMessage: string,
    technicalDetails?: unknown
): AppError {
    return new AppError(code, userMessage, technicalDetails);
}

/**
 * Helper to create a failed ActionResult with nested error object
 * Use this instead of manually constructing { success: false, error: { code, message } }
 */
export function createActionError(
    code: ErrorCodeType,
    message?: string
): { success: false; error: ActionError } {
    return { success: false, error: { code, message } };
}

/**
 * Type guard to check if a value is an ActionError response (new nested structure)
 */
export function isActionError(value: unknown): value is { success: false; error: ActionError } {
    return (
        typeof value === 'object' &&
        value !== null &&
        'success' in value &&
        (value as Record<string, unknown>).success === false &&
        'error' in value &&
        typeof (value as Record<string, unknown>).error === 'object' &&
        (value as Record<string, unknown>).error !== null &&
        'code' in ((value as Record<string, unknown>).error as object)
    );
}


/**
 * Centralized user-friendly error messages by code
 * 
 * These messages are shown to users and should be clear and helpful.
 */
export const ErrorMessages: Record<ErrorCodeType, string> = {
    // Auth
    [ErrorCode.UNAUTHORIZED]: 'You are not authorized to perform this action.',
    [ErrorCode.FORBIDDEN]: 'You do not have permission to access this resource.',
    [ErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please sign in again.',
    [ErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password.',

    // Validation
    [ErrorCode.VALIDATION_FAILED]: 'The submitted data is invalid. Please check your inputs.',
    [ErrorCode.INVALID_INPUT]: 'One or more fields contain invalid values.',

    // Database
    [ErrorCode.NOT_FOUND]: 'The requested resource was not found.',
    [ErrorCode.DUPLICATE_RECORD]: 'This record already exists.',
    [ErrorCode.EMAIL_TAKEN]: 'This email address is already registered. Please use a different email.',
    [ErrorCode.PHONE_TAKEN]: 'This phone number is already registered. Please use a different phone number.',
    [ErrorCode.FOREIGN_KEY_CONSTRAINT]: 'The selected item is no longer available. Please refresh and try again.',
    [ErrorCode.DATABASE_ERROR]: 'A database error occurred. Please try again later.',

    // Account Status
    [ErrorCode.ACCOUNT_INACTIVE]: 'Your account is currently inactive. Please contact your administrator.',

    // Brand / Multi-tenancy
    [ErrorCode.BRAND_ACCESS_DENIED]: 'Access denied: This resource belongs to another brand.',
    [ErrorCode.FORM_SOURCE_UNMAPPED]: "This website isn't linked to a brand yet. Assign one in Websites & sources.",

    // Email & Configuration
    [ErrorCode.EMAIL_NOT_CONFIGURED]: 'Email settings are not configured for this brand.',
    [ErrorCode.EMAIL_SEND_FAILED]: 'Failed to send email. Please try again later.',
    [ErrorCode.CONFIGURATION_MISSING]: 'Required configuration is missing. Please check your settings.',

    // Subscription Business Logic
    [ErrorCode.SUBSCRIPTION_EXISTS]: 'This customer already has an active subscription.',
    [ErrorCode.TRIAL_ALREADY_USED]: 'This customer has already used a trial.',
    [ErrorCode.USERNAME_TAKEN]: 'This username is already taken. Please choose a different username.',
    [ErrorCode.M3U_URL_REQUIRED]: 'M3U URL is required for this subscription type.',
    [ErrorCode.CUSTOMER_NO_EMAIL]: 'Customer does not have an email address.',
    [ErrorCode.ALREADY_RESENT]: 'This email was sent a few minutes ago.',
    [ErrorCode.SEND_IN_PROGRESS]: "This email is still being sent, or its outcome isn't known yet. Try again in a few minutes.",

    // Email templates
    [ErrorCode.TEMPLATE_NOT_FOUND]: 'The selected email template was not found.',

    // Network
    [ErrorCode.NETWORK_ERROR]: 'Network error. Please check your connection and try again.',
    [ErrorCode.TIMEOUT]: 'The request timed out. Please try again.',
    [ErrorCode.SMTP_PRE_ACCEPTANCE_TIMEOUT]: 'The email provider timed out before accepting the message. Please try again.',
    [ErrorCode.EXTERNAL_SERVICE_ERROR]: 'An external service is unavailable. Please try again later.',
    [ErrorCode.FORMS_UNAVAILABLE]: 'Forms data is currently unavailable. Please try again shortly.',

    // Generic
    [ErrorCode.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
};

/**
 * Normalize incoming error codes into stable app-level ErrorCode values.
 * Accepts either app error codes or raw Prisma codes (e.g. P2002).
 */
export function normalizeErrorCode(code: ErrorCodeType | string): ErrorCodeType {
    if (!code) {
        return ErrorCode.UNKNOWN_ERROR;
    }

    if (/^P\d{4}$/i.test(code)) {
        return mapPrismaErrorCode(code.toUpperCase());
    }

    return code in ErrorMessages ? (code as ErrorCodeType) : ErrorCode.UNKNOWN_ERROR;
}

/**
 * Get a user-friendly message for an application or Prisma error code.
 */
export function getErrorMessage(code: ErrorCodeType | string): string {
    return ErrorMessages[normalizeErrorCode(code)];
}

/**
 * Map Prisma error codes to AppError codes
 */
export function mapPrismaErrorCode(prismaCode: string): ErrorCodeType {
    switch (prismaCode) {
        case 'P2002': // Unique constraint violation
            return ErrorCode.DUPLICATE_RECORD;
        case 'P2003': // Foreign key constraint violation
            return ErrorCode.FOREIGN_KEY_CONSTRAINT;
        case 'P2025': // Record not found
            return ErrorCode.NOT_FOUND;
        default:
            return ErrorCode.UNKNOWN_ERROR;
    }
}

/**
 * Helper to create a failed ActionResult from a Prisma error code.
 * Keeps app-level error code stable while using centralized user-facing messages.
 */
export function createPrismaActionError(prismaCode: string): { success: false; error: ActionError } {
    const appCode = normalizeErrorCode(prismaCode);
    return createActionError(appCode, getErrorMessage(appCode));
}
